import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { MicroscopeIcon } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

import { api, type ItemAnalysisRow, type NotMeaningful } from '@/lib/api';
import { absenceOf, type StatAbsence } from '@/lib/analysis';
import {
  useContent, useDateTime, useLabel, useNumber, usePercent, useT, useTn,
} from '@/lib/i18n';
import { useUrlState } from '@/lib/url-state';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { DataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig,
} from '@/components/ui/chart';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

/**
 * Item analysis — what makes *certified* a claim rather than a state
 * (`plan-preproduction` M1).
 *
 * ### WHY THIS IS ITS OWN SCREEN, AND NOT A TAB
 *
 * The plan asks that it be reachable *"from where a psychometrician already
 * works"*, and the two candidates were a tab on Modules and a tab on the item
 * bank. Neither fits, for the same reason:
 *
 *   - **Modules** is a card grid of the whole catalogue with no per-module
 *     detail view to hang a tab on. Adding one would mean building a module
 *     detail screen first, and this analysis would be a paragraph on it.
 *   - **Questions** is the item bank across ALL modules, and an item analysis is
 *     computed per module by construction — a p-value from one instrument and a
 *     mean response from another share no scale. A tab there would have needed
 *     its own module filter, at which point it is this screen wearing someone
 *     else's page header.
 *
 * So it is a screen in the **Assessment Design** group, beside Modules,
 * Questions and Scoring — the four things a psychometrician moves between — and
 * every module card links straight into it with `?moduleId=` prefilled. Reaching
 * it takes one click from where the work already is, which is what "do not
 * strand it" asks for; owning its own filters is what lets it be honest about
 * the population it is describing.
 *
 * ### THE THREE ABSENCES ARE THE SCREEN
 *
 * `contract/analysis.ts` models three separate reasons a statistic is not there,
 * and the whole point of that modelling is lost if a console renders one blank
 * cell for all of them. So `<Missing>` below renders three visibly different
 * things and each says which it is:
 *
 *   1. **Not defined for this type** — permanent. A ranking item has no central
 *      tendency because its response is an ordering of option ids; no amount of
 *      data will produce one. Drawn as a bordered badge, because it is a
 *      property of the item rather than a state of the sample.
 *   2. **`{answered} of {minimum} answers`** — too few responses. Fixed by more
 *      data, and the number says how much more.
 *   3. **No variance to measure** — enough data, defined for the type, and still
 *      absent: everybody answered alike. Reporting `0` here would read as *"this
 *      item does not discriminate"*, which is a finding, when the truth is that
 *      it could not be measured.
 *
 * Below the floor the COUNTS still render and only the estimates are withheld —
 * `delivered` and `answered` are observations, not estimates, and hiding them
 * would remove the very number that says when to look again.
 *
 * ### `central` IS A NAMED STATISTIC
 *
 * `ItemStatistic` exists because a `p_value` and a `mean_response` are different
 * measurements, and the server refused to flatten them into one number. A column
 * headed "Central tendency" carrying bare numbers would re-create exactly that
 * confusion one layer up, so **every cell names its own statistic above the
 * value** and carries the scale it sits on. The column header is the question;
 * the cell is the answer, and the answer includes which question was asked.
 */

const ALL = '__all__';

/* ------------------------------------------------------------------------ */
/*  The three absences                                                       */
/* ------------------------------------------------------------------------ */

/**
 * Why a statistic is not on screen — rendered so the three reasons cannot be
 * mistaken for each other, or for a rendering bug.
 *
 * `title` carries the longer explanation rather than a tooltip component: this
 * appears in up to sixty table cells and a popover per cell would be a worse
 * screen, not a better one.
 */
function Missing({
  absence, itemType, answered, minimum,
}: {
  absence: Exclude<StatAbsence, 'present'>;
  itemType: string;
  answered: number;
  minimum: number;
}) {
  const t = useT();
  const label = useLabel();
  const number = useNumber();

  if (absence === 'not_meaningful') {
    return (
      <Badge variant="outline" title={t('absence.not_meaningful.hint', { type: label('itemType', itemType) })}>
        {t('absence.not_meaningful')}
      </Badge>
    );
  }

  if (absence === 'insufficient') {
    return (
      <span
        className="text-muted-foreground text-xs tabular-nums"
        title={t('absence.insufficient.hint')}
      >
        {t('absence.insufficient', { answered: number(answered), minimum: number(minimum) })}
      </span>
    );
  }

  return (
    <span className="text-muted-foreground text-xs italic" title={t('absence.no_variance.hint')}>
      {t('absence.no_variance')}
    </span>
  );
}

/**
 * One statistic cell: the number, or the reason there is not one.
 *
 * The classification lives in `lib/analysis.ts` so that every column asks the
 * question the same way — re-deriving "is this missing because of the type or
 * because of the sample" per column is how two columns end up disagreeing.
 */
function Statistic({
  row, statistic, minimum, children,
}: {
  row: ItemAnalysisRow;
  statistic: NotMeaningful;
  minimum: number;
  /** Rendered only when the value is present. */
  children: ReactNode;
}) {
  const value = statistic === 'central' ? row.central : row.itemTotalCorrelation;
  const absence = absenceOf(row, statistic, value);

  if (absence === 'present') return <>{children}</>;
  return (
    <Missing absence={absence} itemType={row.itemType} answered={row.answered} minimum={minimum} />
  );
}

/* ------------------------------------------------------------------------ */
/*  The per-item option distribution                                         */
/* ------------------------------------------------------------------------ */

/**
 * One item's option distribution — a chart AND its table, never one alone.
 *
 * SINGLE SERIES (respondents per option), so one hue from the design system and
 * no categorical palette. The bars are deliberately not ramped by height: that
 * would double-encode the length as colour and burn the only free channel on
 * information the bars already carry.
 *
 * The labels are option IDS, because that is what `ItemOptionStat` carries — the
 * authored option text lives on the item and `GET /api/questions/:id` already
 * serves it. Inventing a second copy of it on the analysis payload was rejected
 * server-side, and inventing a fetch-per-row here would rebuild the per-item
 * query loop `02` warns about.
 */
function OptionDistribution({ row, minimum }: { row: ItemAnalysisRow; minimum: number }) {
  const t = useT();
  const number = useNumber();
  const options = row.options ?? [];
  // Field presence, never an item-type name: `meanRank` is what a ranking
  // distribution carries and nothing else does.
  const ranked = options.some((o) => o.meanRank !== undefined);

  const config = {
    count: { label: t('itemAnalysis.column.chosen'), color: 'var(--chart-1)' },
  } satisfies ChartConfig;

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t('itemAnalysis.detail.title', { item: row.itemId })}</DialogTitle>
        <DialogDescription>{t('itemAnalysis.detail.description')}</DialogDescription>
      </DialogHeader>

      {options.length === 0 ? (
        <Missing
          absence={
            (row.notMeaningful ?? []).includes('option_distribution')
              ? 'not_meaningful'
              : row.sufficient ? 'no_variance' : 'insufficient'
          }
          itemType={row.itemType}
          answered={row.answered}
          minimum={minimum}
        />
      ) : (
        <div className="space-y-4">
          <ChartContainer config={config} className="max-h-64 w-full">
            <BarChart data={options} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid horizontal={false} stroke="var(--border)" />
              <XAxis type="number" allowDecimals={false} tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }} />
              <YAxis
                type="category"
                dataKey="optionId"
                width={120}
                tickLine={false}
                axisLine={false}
                tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="count" fill="var(--color-count)" radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ChartContainer>

          <Table>
            <caption className="text-muted-foreground caption-bottom pt-2 text-xs">
              {t('itemAnalysis.detail.options')}
            </caption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('itemAnalysis.column.option')}</TableHead>
                <TableHead className="w-24 text-end">{t('itemAnalysis.column.chosen')}</TableHead>
                {ranked && (
                  <TableHead className="w-28 text-end">{t('itemAnalysis.column.meanRank')}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {options.map((option) => (
                <TableRow key={option.optionId}>
                  <TableCell className="font-mono text-xs" dir="ltr">{option.optionId}</TableCell>
                  <TableCell className="text-end font-medium tabular-nums">
                    {number(option.count)}
                  </TableCell>
                  {ranked && (
                    <TableCell className="text-end tabular-nums">
                      {option.meanRank === undefined ? '—' : number(option.meanRank)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {ranked && (
            <p className="text-muted-foreground text-xs">{t('itemAnalysis.detail.rankingNote')}</p>
          )}
        </div>
      )}
    </DialogContent>
  );
}

/* ------------------------------------------------------------------------ */
/*  The screen                                                               */
/* ------------------------------------------------------------------------ */

export function ItemAnalysis() {
  const t = useT();
  const tn = useTn();
  const label = useLabel();
  const number = useNumber();
  const percent = usePercent();
  const content = useContent();
  const dateTime = useDateTime();

  /**
   * The filters live in the URL, like every other list in this console — and
   * here it is load-bearing rather than conventional: a module card links
   * straight in with `?moduleId=`, and a psychometrician sharing "these are the
   * items that misbehave in the cleaned analysis" is sharing a link.
   */
  const [{ moduleId, personaId, held }, setFilters] = useUrlState({
    moduleId: '',
    personaId: ALL,
    held: 'yes',
  });
  const includeHeld = held !== 'no';

  const [openRow, setOpenRow] = useState<ItemAnalysisRow | null>(null);

  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas });

  const analysis = useQuery({
    queryKey: ['item-analysis', moduleId, personaId, includeHeld],
    queryFn: () =>
      api.itemAnalysis(moduleId, {
        ...(personaId === ALL ? {} : { personaId }),
        includeHeld,
      }),
    // A module has to be chosen before there is anything to ask for. Firing the
    // request anyway would answer 404 and render an error where the honest
    // screen is "choose a module".
    enabled: moduleId !== '',
  });

  const minimum = analysis.data?.minimumN ?? 0;
  const rows = useMemo(() => analysis.data?.items ?? [], [analysis.data]);

  const columns = useMemo<ColumnDef<ItemAnalysisRow, unknown>[]>(
    () => [
      {
        header: t('itemAnalysis.column.item'),
        accessorKey: 'itemId',
        cell: ({ row }) => (
          <div>
            <p className="font-mono text-xs" dir="ltr">{row.original.itemId}</p>
            <p className="text-muted-foreground text-xs">
              {label('itemType', row.original.itemType)}
            </p>
          </div>
        ),
      },
      {
        header: t('itemAnalysis.column.responses'),
        accessorKey: 'answered',
        cell: ({ row }) => (
          <div>
            {/* Counts are observations, not estimates. They render below the
                floor too — they are what says how far off it is. */}
            <p className="text-sm tabular-nums">
              {t('itemAnalysis.answeredOf', {
                answered: number(row.original.answered),
                delivered: number(row.original.delivered),
              })}
            </p>
            {!row.original.sufficient && (
              <p className="text-muted-foreground text-xs tabular-nums">
                {t('absence.insufficient', {
                  answered: number(row.original.answered),
                  minimum: number(minimum),
                })}
              </p>
            )}
          </div>
        ),
      },
      {
        header: t('itemAnalysis.column.central'),
        accessorKey: 'central',
        cell: ({ row }) => (
          <Statistic row={row.original} statistic="central" minimum={minimum}>
            {row.original.central && (
              <div>
                {/* THE STATISTIC NAMES ITSELF. A p-value and a mean response
                    must never be read off one heading. */}
                <p
                  className="text-muted-foreground text-xs"
                  title={label(`itemStatistic.${row.original.central.statistic}`, 'hint')}
                >
                  {label('itemStatistic', row.original.central.statistic)}
                </p>
                <p className="text-sm font-medium tabular-nums">
                  {number(row.original.central.value)}
                  {row.original.central.scaleMin !== undefined
                    && row.original.central.scaleMax !== undefined && (
                    <span className="text-muted-foreground ms-1 font-normal">
                      {t('itemAnalysis.scale', {
                        min: number(row.original.central.scaleMin),
                        max: number(row.original.central.scaleMax),
                      })}
                    </span>
                  )}
                </p>
                {row.original.central.sd !== undefined && (
                  <p className="text-muted-foreground text-xs tabular-nums">
                    {t('itemAnalysis.sd', { sd: number(row.original.central.sd) })}
                  </p>
                )}
              </div>
            )}
          </Statistic>
        ),
      },
      {
        header: t('itemAnalysis.column.correlation'),
        accessorKey: 'itemTotalCorrelation',
        cell: ({ row }) => (
          <Statistic row={row.original} statistic="item_total_correlation" minimum={minimum}>
            <span
              className="text-sm font-medium tabular-nums"
              title={t('itemAnalysis.correlation.hint')}
            >
              {row.original.itemTotalCorrelation !== undefined
                && number(row.original.itemTotalCorrelation)}
            </span>
          </Statistic>
        ),
      },
      {
        header: t('itemAnalysis.column.timing'),
        accessorKey: 'timing',
        cell: ({ row }) => {
          const timing = row.original.timing;
          // Timing is neither estimated nor keyed to an item type — it is
          // present exactly when responses carried a time, so it needs none of
          // the three-absence machinery.
          if (!timing) {
            return (
              <span className="text-muted-foreground text-xs">{t('itemAnalysis.timing.none')}</span>
            );
          }
          return (
            <div>
              <p className="text-sm tabular-nums">{t('qcEvidence.ms', { ms: number(timing.medianMs) })}</p>
              <p className="text-muted-foreground text-xs tabular-nums">
                {t('itemAnalysis.timing.quartiles', {
                  p25: number(timing.p25Ms),
                  p75: number(timing.p75Ms),
                })}
              </p>
            </div>
          );
        },
      },
      {
        header: t('itemAnalysis.column.qcHold'),
        accessorKey: 'qcHeldRuns',
        cell: ({ row }) => {
          /**
           * OMITTED IS NOT ZERO. With held runs excluded from the population the
           * rate is 0 by construction, so the server withholds it — and a
           * console printing `0%` there would report a cleaned analysis as a
           * clean bank, which is the exact inversion of what the filter did.
           */
          if (row.original.qcHoldRate === undefined) {
            return !includeHeld ? (
              <Badge variant="secondary" title={t('itemAnalysis.qcHold.excluded.hint')}>
                {t('itemAnalysis.qcHold.excluded')}
              </Badge>
            ) : (
              <Missing
                absence={row.original.sufficient ? 'no_variance' : 'insufficient'}
                itemType={row.original.itemType}
                answered={row.original.answered}
                minimum={minimum}
              />
            );
          }
          return (
            <div>
              <p className="text-sm font-medium">{percent(row.original.qcHoldRate * 100, 1)}</p>
              <p className="text-muted-foreground text-xs">
                {tn('itemAnalysis.qcHold.runs', row.original.qcHeldRuns)}
              </p>
            </div>
          );
        },
      },
    ],
    [t, tn, label, number, percent, minimum, includeHeld],
  );

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('itemAnalysis.title')}
        description={t('itemAnalysis.description')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* `value=""` is Radix's own "nothing chosen": its
                `shouldShowPlaceholder` treats an empty string exactly like
                `undefined`, so the select stays controlled from the first render
                rather than flipping uncontrolled→controlled on the first pick. */}
            <Select value={moduleId} onValueChange={(next) => setFilters({ moduleId: next })}>
              <SelectTrigger className="w-52" aria-label={t('dashboard.filterByModule')}>
                <SelectValue placeholder={t('itemAnalysis.pickModule.title')} />
              </SelectTrigger>
              <SelectContent>
                {(modules.data?.items ?? []).map((m) => (
                  <SelectItem key={m.moduleId} value={m.moduleId}>{content(m.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={personaId} onValueChange={(next) => setFilters({ personaId: next })}>
              <SelectTrigger className="w-44" aria-label={t('analytics.filterByPersona')}>
                <SelectValue placeholder={t('analytics.allPersonas')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('analytics.allPersonas')}</SelectItem>
                {(personas.data?.items ?? []).map((p) => (
                  <SelectItem key={p.personaId} value={p.personaId}>{content(p.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2" title={t('itemAnalysis.includeHeld.hint')}>
              <Switch
                id="include-held"
                checked={includeHeld}
                onCheckedChange={(on) => setFilters({ held: on ? 'yes' : 'no' })}
              />
              <Label htmlFor="include-held" className="text-xs font-normal">
                {t('itemAnalysis.includeHeld')}
              </Label>
            </div>
          </div>
        }
      />

      {moduleId === '' ? (
        <EmptyState
          icon={MicroscopeIcon}
          title={t('itemAnalysis.pickModule.title')}
          description={t('itemAnalysis.pickModule.description')}
        />
      ) : (
        <>
          {analysis.data && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{tn('itemAnalysis.runs', analysis.data.runs)}</Badge>
              <Badge variant="secondary">{tn('itemAnalysis.items', analysis.data.items.length)}</Badge>
              <span className="text-muted-foreground text-xs">
                {t('itemAnalysis.asOf', { at: dateTime(analysis.data.asOf) })}
              </span>
            </div>
          )}

          {/* The floor is stated ONCE, above the table, rather than repeated in
              every withheld cell — the cells say how far each item is from it. */}
          {analysis.data && (
            <p className="text-muted-foreground mb-4 text-xs">
              {t('itemAnalysis.floor', { minimum: number(minimum) })}
            </p>
          )}

          <DataTable
            columns={columns}
            rows={rows}
            isLoading={analysis.isLoading}
            error={analysis.error}
            onRetry={() => void analysis.refetch()}
            emptyTitle={t('itemAnalysis.empty.title')}
            emptyDescription={t('itemAnalysis.empty.description')}
            onRowClick={(row) => setOpenRow(row)}
          />
        </>
      )}

      <Dialog open={openRow !== null} onOpenChange={(open) => !open && setOpenRow(null)}>
        {openRow && <OptionDistribution row={openRow} minimum={minimum} />}
      </Dialog>
    </div>
  );
}
